import React, { useEffect, useRef, useState } from 'react';

const INTRO_PARAGRAPHS = [
  'Welcome to Flarix.ai.',
  <>These Terms &amp; Conditions ("Terms", "Terms and Conditions", or "Agreement") govern your access to and use of the Flarix.ai website, applications, software, AI-powered tools, floor-planning services, design services, digital products, and related services (collectively, the "Platform").</>,
  <>The Platform is operated by <strong className="tc-placeholder">flarix</strong>, having its registered office at <strong className="tc-placeholder">608, Lotus divine, Shivalaya, Bijalpur, Indore</strong> ("Flarix", "Company", "we", "us", or "our").</>,
  'By accessing the Platform, creating an account, submitting information, purchasing a product or service, or otherwise using Flarix.ai, you acknowledge that you have read, understood, and agreed to these Terms.',
  'If you do not agree with these Terms, please do not use the Platform.',
];

const TERMS_SECTIONS = [
  {
    number: '01',
    title: 'Eligibility',
    blocks: [
      { p: 'You must be legally capable of entering into a binding agreement to use the Platform.' },
      { p: 'By using Flarix.ai, you represent that:' },
      { list: ['You are at least 18 years of age; or', 'You are using the Platform with the involvement and consent of a parent or legal guardian where permitted by applicable law;', 'The information you provide is accurate and complete; and', 'You have the legal authority to enter into these Terms.'] },
    ],
  },
  {
    number: '02',
    title: 'About Flarix.ai',
    blocks: [
      { p: 'Flarix.ai provides AI-powered tools and digital services intended to assist users with preliminary floor planning, space planning, architectural concepts, visualization, and related design requirements.' },
      { p: 'Depending on the product purchased, Flarix may generate or provide:' },
      { list: ['AI-generated floor plans', 'Room layouts', 'Space-planning suggestions', '2D layouts', '3D visualizations', 'Design concepts', 'Design recommendations', 'Vastu-oriented suggestions', 'Downloadable digital files', 'Reports', 'Other digital design outputs.'] },
      { p: 'The exact features and deliverables may vary between products and packages.' },
    ],
  },
  {
    number: '03',
    title: 'AI-Generated Outputs',
    blocks: [
      { p: 'Flarix.ai uses artificial intelligence and automated technologies to generate certain outputs.' },
      { p: 'You acknowledge that AI-generated content may contain errors, inaccuracies, omissions, inconsistencies, or outputs that may not be suitable for your particular requirements.' },
      { p: 'Flarix does not guarantee that an AI-generated design will be:' },
      { list: ['Technically accurate', 'Structurally feasible', 'Architecturally optimal', 'Suitable for construction', 'Compliant with local regulations', 'Suitable for statutory approval', 'Suitable for a particular site', 'Free from errors', 'Appropriate for your specific requirements.'] },
      { p: 'You are responsible for reviewing the output and obtaining professional advice before using it for construction or other consequential purposes.' },
    ],
  },
  {
    number: '04',
    title: 'User-Provided Information',
    blocks: [
      { p: 'The quality and suitability of an AI-generated design may depend on the information supplied by the user.' },
      { p: 'You agree to provide accurate and complete information, including where applicable:' },
      { list: ['Plot dimensions', 'Plot shape', 'Road information', 'North direction', 'Number of floors', 'Required rooms', 'Family requirements', 'Parking requirements', 'Setback information', 'Location', 'Site information', 'Other information requested by the Platform.'] },
      { p: 'Flarix shall not be responsible for errors resulting from inaccurate, incomplete, misleading, or outdated information provided by the user.' },
    ],
  },
  {
    number: '05',
    title: 'Design Review and Professional Approval',
    blocks: [
      { p: 'Flarix.ai is not a replacement for qualified architectural, structural, engineering, legal, or regulatory professionals.' },
      { p: 'Before commencing construction, you should have the relevant designs reviewed by an appropriately qualified professional.' },
      { p: 'You are responsible for determining whether the design complies with all applicable:' },
      { list: ['Municipal regulations', 'Building bye-laws', 'Development regulations', 'Zoning requirements', 'Structural requirements', 'Fire-safety requirements', 'Accessibility requirements', 'Environmental requirements', 'Local authority requirements', 'Other applicable laws and regulations.'] },
      { p: 'Flarix does not guarantee approval by any government authority, municipality, development authority, housing society, or other organization.' },
    ],
  },
  {
    number: '06',
    title: 'Account Registration',
    blocks: [
      { p: 'Certain features may require you to create an account.' },
      { p: 'You agree to:' },
      { list: ['Provide accurate information', 'Maintain the confidentiality of your login credentials', 'Not share your account in a manner that compromises its security', 'Immediately notify Flarix of unauthorized access', 'Accept responsibility for activities conducted through your account.'] },
      { p: 'Flarix reserves the right to suspend or terminate accounts that contain false information, violate these Terms, or are used for unlawful purposes.' },
    ],
  },
  {
    number: '07',
    title: 'Products and Services',
    blocks: [
      { p: 'Flarix may offer different products, packages, subscriptions, or services.' },
      { p: 'Before purchasing, you should review the applicable:' },
      { list: ['Product description', 'Price', 'Deliverables', 'Delivery method', 'Number of revisions, if applicable', 'Validity period, if applicable', 'Other applicable conditions.'] },
      { p: 'Flarix reserves the right to modify, discontinue, replace, or update any product or service.' },
      { p: 'Where a product has already been purchased, the applicable terms presented at the time of purchase shall generally govern that transaction, subject to applicable law.' },
    ],
  },
  {
    number: '08',
    title: 'Pricing',
    blocks: [
      { p: 'All prices displayed on the Platform are in the currency specified at the time of purchase.' },
      { p: 'Unless otherwise stated, applicable taxes, including GST or other government charges, may be added to the displayed price.' },
      { p: 'Flarix reserves the right to change prices, offers, discounts, and promotional packages at any time.' },
      { p: 'A price change will not affect an order that has already been successfully completed, except where required by applicable law.' },
    ],
  },
  {
    number: '09',
    title: 'Payment',
    blocks: [
      { p: 'Payments may be processed through third-party payment gateways.' },
      { p: 'By making a payment, you authorize the applicable payment provider to process the transaction.' },
      { p: 'Flarix is not responsible for failures caused solely by a third-party payment gateway, bank, card issuer, or payment network.' },
      { p: 'You agree not to initiate an illegitimate chargeback or payment dispute after receiving the purchased digital product or service.' },
      { p: 'Nothing in this clause limits your rights under applicable consumer or payment laws.' },
    ],
  },
  {
    number: '10',
    title: 'Digital Delivery',
    blocks: [
      { p: 'Many Flarix.ai products are digital products delivered electronically.' },
      { p: 'Delivery may occur through:' },
      { list: ['Your account', 'Email', 'Download links', 'The Platform', 'Other electronic means', 'A combination of these methods.'] },
      { p: 'Because certain products may be generated and delivered immediately or shortly after purchase, you acknowledge that the service may begin immediately after payment.' },
      { p: 'You are responsible for providing a valid email address and maintaining access to the device, internet connection, and software necessary to receive and use the digital product.' },
    ],
  },
  {
    number: '11',
    title: 'Refund and Cancellation Policy',
    blocks: [
      { warn: <>Because Flarix.ai may provide digitally generated products that are created or delivered immediately after purchase, orders for digital products may generally be non-refundable once generation, processing, or delivery has commenced, except where otherwise required by applicable law or expressly stated for a particular product.</> },
      { p: 'If you experience a genuine technical issue, duplicate payment, failed delivery, or other legitimate problem, you may contact Flarix support for assistance.' },
      { p: 'Where appropriate, Flarix may, at its discretion:' },
      { list: ['Correct the issue', 'Regenerate the output', 'Provide a replacement', 'Provide an alternative solution', 'Issue a refund.'] },
      { p: 'Nothing in this section limits any mandatory rights available to consumers under applicable law.' },
    ],
  },
  {
    number: '12',
    title: 'Revisions and Modifications',
    blocks: [
      { p: 'Where a particular package includes revisions, the number and scope of revisions will be specified in the relevant product description.' },
      { p: 'Unless expressly included in the purchased package, Flarix is not obligated to provide unlimited revisions or modifications.' },
      { p: 'Changes resulting from incorrect information supplied by the user may be treated separately from revisions included in the purchased package.' },
    ],
  },
  {
    number: '13',
    title: 'Intellectual Property',
    blocks: [
      { p: 'All rights, title, and interest in the Platform and its underlying technology remain with Flarix or its licensors.' },
      { p: 'This includes, without limitation:' },
      { list: ['Website content', 'Software', 'Source code', 'AI systems', 'Algorithms', 'Models', 'Databases', 'Branding', 'Logos', 'Graphics', 'User interfaces', 'Templates', 'Text', 'Design systems', 'Platform architecture', 'Other proprietary materials.'] },
      { p: "Except as expressly permitted, you may not copy, reproduce, modify, distribute, reverse engineer, sell, license, scrape, or commercially exploit Flarix's proprietary technology or content." },
    ],
  },
  {
    number: '14',
    title: 'License to User-Generated Outputs',
    blocks: [
      { p: 'Subject to your compliance with these Terms and payment of applicable fees, Flarix grants you a limited, non-exclusive, non-transferable license to use the purchased design output for your personal or project-related purposes.' },
      { p: 'Unless expressly permitted by Flarix, you may not:' },
      { list: ['Resell the output as a standalone product', 'Upload it to a competing design marketplace for resale', "Represent Flarix's output as your own proprietary software or technology", 'Use Flarix outputs to train or develop a competing AI system', 'Mass distribute the output', "Commercially exploit Flarix's underlying technology."] },
      { p: 'Flarix may retain rights in its underlying templates, systems, processes, software, AI technology, and other proprietary components used to generate the output.' },
    ],
  },
  {
    number: '15',
    title: 'User Content',
    blocks: [
      { p: <>You may provide information, photographs, drawings, documents, requirements, descriptions, or other materials ("User Content") through the Platform.</> },
      { p: 'You retain ownership of your User Content, subject to the rights necessary for Flarix to provide the services.' },
      { p: 'By submitting User Content, you grant Flarix a limited license to host, process, reproduce, transmit, modify, and use such content as reasonably necessary to:' },
      { list: ['Provide the requested service', 'Generate the requested design', 'Improve Platform functionality', 'Provide customer support', 'Detect fraud or misuse', 'Operate and maintain the Platform.'] },
      { p: 'You represent that you have the necessary rights and permissions to submit such content.' },
    ],
  },
  {
    number: '16',
    title: 'AI and Third-Party Technology',
    blocks: [
      { p: 'Flarix may use third-party technologies, APIs, cloud infrastructure, AI models, payment processors, analytics services, hosting providers, or other service providers.' },
      { p: 'The availability and performance of certain features may therefore depend on third-party services.' },
      { p: "Flarix is not responsible for interruptions or failures caused by third-party services outside its reasonable control." },
    ],
  },
  {
    number: '17',
    title: 'Prohibited Use',
    blocks: [
      { p: 'You agree not to use Flarix.ai to:' },
      { list: ['Violate any applicable law', 'Commit fraud', 'Impersonate another person', 'Upload unlawful or infringing material', 'Attempt unauthorized access to the Platform', 'Introduce malware or malicious code', 'Interfere with Platform operations', 'Scrape or systematically extract Platform data without permission', 'Reverse engineer proprietary systems', 'Circumvent security controls', 'Abuse promotional offers', 'Create fraudulent accounts', 'Submit intentionally false information', 'Use the Platform to develop a competing service through unauthorized extraction or copying.'] },
      { p: 'Flarix may suspend or terminate access where it reasonably believes that prohibited activity has occurred.' },
    ],
  },
  {
    number: '18',
    title: 'Third-Party Services',
    blocks: [
      { p: 'The Platform may contain links, integrations, advertisements, or references to third-party products and services.' },
      { p: 'Third-party services are governed by their own terms and privacy policies.' },
      { p: 'Flarix does not control and is not responsible for third-party websites, products, services, content, pricing, availability, or performance.' },
    ],
  },
  {
    number: '19',
    title: 'Disclaimer of Warranties',
    blocks: [
      { p: <>To the maximum extent permitted by applicable law, the Platform and its services are provided on an "as is" and "as available" basis.</> },
      { p: 'Flarix does not warrant that:' },
      { list: ['The Platform will always be available', 'The Platform will be error-free', 'AI-generated outputs will always be accurate', 'Designs will satisfy every user requirement', 'Designs will comply with local regulations', 'Designs will be approved by authorities', 'The Platform will be uninterrupted', 'Any particular result will be achieved', 'The Platform will be suitable for every project or purpose.'] },
    ],
  },
  {
    number: '20',
    title: 'Limitation of Liability',
    blocks: [
      { p: 'To the maximum extent permitted by applicable law, Flarix shall not be liable for indirect, incidental, consequential, special, exemplary, or punitive damages arising from or relating to your use of the Platform.' },
      { p: 'This may include losses arising from:' },
      { list: ['Construction undertaken using an unverified design', 'Design or measurement errors', 'Contractor decisions', 'Material procurement', 'Delays', 'Cost overruns', 'Regulatory rejection', 'Loss of profits', 'Loss of business', 'Loss of data', 'Reliance on AI-generated information without professional verification.'] },
      { p: "To the maximum extent permitted by applicable law, Flarix's aggregate liability arising from a particular purchase or service shall not exceed the amount actually paid by the user for that particular service, except where such limitation is prohibited by applicable law." },
      { p: 'Nothing in these Terms excludes or limits liability that cannot legally be excluded or limited.' },
    ],
  },
  {
    number: '21',
    title: 'Indemnification',
    blocks: [
      { p: 'To the extent permitted by applicable law, you agree to indemnify and hold harmless Flarix, its affiliates, directors, officers, employees, contractors, and service providers from claims, losses, liabilities, damages, costs, and expenses arising from:' },
      { list: ['Your violation of these Terms', 'Your misuse of the Platform', 'Your User Content', 'Your violation of third-party rights', 'Your unlawful activities', 'Your use of Flarix outputs contrary to these Terms.'] },
    ],
  },
  {
    number: '22',
    title: 'Suspension and Termination',
    blocks: [
      { p: 'Flarix may suspend, restrict, or terminate your access to the Platform if:' },
      { list: ['You violate these Terms', 'You engage in fraudulent activity', 'You misuse the Platform', 'Your activities create security or legal risks', 'Suspension is required by law.'] },
      { p: 'Termination will not affect rights or obligations that accrued before termination.' },
    ],
  },
  {
    number: '23',
    title: 'Privacy',
    blocks: [
      { p: 'Your use of Flarix.ai is also subject to our Privacy Policy.' },
      { p: 'The Privacy Policy explains how Flarix collects, uses, stores, processes, and protects personal information.' },
      { p: 'By using the Platform, you acknowledge that you have reviewed the applicable Privacy Policy.' },
      { contact: [{ label: 'Privacy Policy', value: '[Insert Privacy Policy URL]' }] },
    ],
  },
  {
    number: '24',
    title: 'Electronic Communications',
    blocks: [
      { p: 'By using Flarix.ai, you consent to receive electronic communications relating to:' },
      { list: ['Your account', 'Purchases', 'Payments', 'Deliveries', 'Customer support', 'Service updates', 'Security notifications', 'Other communications necessary to provide the service.'] },
      { p: 'Marketing communications will be handled in accordance with applicable law and available communication preferences.' },
    ],
  },
  {
    number: '25',
    title: 'Force Majeure',
    blocks: [
      { p: 'Flarix shall not be responsible for delays or failures caused by circumstances beyond its reasonable control, including:' },
      { list: ['Natural disasters', 'Fire', 'Flood', 'War', 'Terrorism', 'Government restrictions', 'Internet failures', 'Telecommunications failures', 'Cloud-service outages', 'Third-party service failures', 'Cyber incidents', 'Strikes', 'Other events beyond reasonable control.'] },
    ],
  },
  {
    number: '26',
    title: 'Changes to These Terms',
    blocks: [
      { p: 'Flarix may update these Terms from time to time.' },
      { p: <>The updated Terms will be published on the Platform with a revised "Last Updated" date.</> },
      { p: 'Your continued use of Flarix.ai after the updated Terms become effective constitutes acceptance of the updated Terms, to the extent permitted by applicable law.' },
    ],
  },
  {
    number: '27',
    title: 'Governing Law',
    blocks: [
      { p: 'These Terms shall be governed by and interpreted in accordance with the laws of India, without regard to conflict-of-law principles.' },
      { p: <>Subject to applicable consumer protection and other mandatory laws, disputes arising from these Terms or your use of Flarix.ai shall be subject to the jurisdiction of the courts located in <strong className="tc-placeholder">[Indore, Madhya Pradesh, India]</strong>.</> },
    ],
  },
  {
    number: '28',
    title: 'Severability',
    blocks: [
      { p: 'If any provision of these Terms is determined to be invalid, unlawful, or unenforceable, that provision shall be modified or removed to the minimum extent necessary, and the remaining provisions shall continue in full force and effect.' },
    ],
  },
  {
    number: '29',
    title: 'Entire Agreement',
    blocks: [
      { p: 'These Terms, together with the Privacy Policy, Disclaimer, applicable product-specific terms, and other policies expressly incorporated into these Terms, constitute the agreement between you and Flarix regarding your use of the Platform.' },
    ],
  },
  {
    number: '30',
    title: 'Contact Us',
    blocks: [
      { p: 'If you have questions, concerns, complaints, or requests relating to these Terms, please contact:' },
      { contact: [
        { label: 'Flarix.ai', value: '' },
        { label: 'Legal Entity', value: 'flarix' },
        { label: 'Registered Address', value: '608, Lotus divine, Shivalaya, Bijalpur, Indore' },
        { label: 'Email', value: 'support@flarix.ai' },
        { label: 'Website', value: 'https://flarix.ai' },
      ] },
    ],
  },
];

const ACCEPTANCE_LEAD = 'By clicking "I Agree", "Continue", "Pay Now", "Purchase", or any equivalent button, or by accessing or using Flarix.ai, you acknowledge that:';
const ACCEPTANCE_ITEMS = [
  'You have read and understood these Terms & Conditions;',
  'You agree to be legally bound by these Terms;',
  'You understand that certain outputs are AI-generated;',
  'You understand that AI-generated designs may require professional verification;',
  'You understand the applicable refund and cancellation conditions; and',
  'You agree to use the Platform in accordance with these Terms.',
];

function renderBlock(block, key) {
  if (block.p) return <p className="tc-text" key={key}>{block.p}</p>;
  if (block.h) return <h3 className="tc-subhead" key={key}>{block.h}</h3>;
  if (block.warn) {
    return (
      <div className="tc-warn" key={key}>
        <span className="tc-warn-badge">!</span>
        <p>{block.warn}</p>
      </div>
    );
  }
  if (block.list) {
    return (
      <ul className="tc-list" key={key}>
        {block.list.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    );
  }
  if (block.contact) {
    return (
      <div className="tc-contact" key={key}>
        {block.contact.map((row, i) =>
          row.value ? (
            <div className="tc-contact-row" key={i}>
              <span className="tc-contact-label">{row.label}</span>
              <span className="tc-contact-value">{row.value}</span>
            </div>
          ) : (
            <div className="tc-contact-title" key={i}>{row.label}</div>
          )
        )}
      </div>
    );
  }
  return null;
}

function TermsConditions() {
  const [activeId, setActiveId] = useState(TERMS_SECTIONS[0].number);
  const sectionRefs = useRef({});

  // Ensure the page always opens from the top, even when navigated to
  // from another route where the browser might otherwise restore a
  // previous scroll position.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!('IntersectionObserver' in window)) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.dataset.number);
          }
        });
      },
      { rootMargin: '-15% 0px -70% 0px', threshold: 0 }
    );
    Object.values(sectionRefs.current).forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="terms-page">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap');

        .terms-page {
          --ink: #0b1d2e;
          --violet: #8b5cf6;
          --cyan: #5ec8e8;
          --cyan-deep: #0e7490;
          --warn: #b45309;
          --warn-bg: #fffaf0;
          --warn-border: #fbd38d;
          --line: rgba(94, 200, 232, 0.14);
          --line-strong: rgba(94, 200, 232, 0.4);
          --text: #2b2740;
          --text-muted: #6b7280;
          --border: #ece9f7;
          --panel: #f7f6fd;
          font-family: 'Poppins', sans-serif;
          background: #fff;
          color: var(--text);
          line-height: 1.75;
          width: 100%;
          overflow-x: hidden;
        }
        .terms-page * {
          box-sizing: border-box;
        }

        .terms-page .tc-hero {
          position: relative;
          overflow: hidden;
          background: var(--ink);
          background-image:
            repeating-linear-gradient(0deg, var(--line) 0 1px, transparent 1px 42px),
            repeating-linear-gradient(90deg, var(--line) 0 1px, transparent 1px 42px);
          padding: 5rem 1.5rem 6.5rem;
          text-align: center;
          border-bottom: 1px solid var(--line-strong);
        }

        .terms-page .tc-corner {
          position: absolute;
          width: 18px;
          height: 18px;
          z-index: 1;
        }
        .terms-page .tc-corner::before {
          content: '';
          position: absolute;
          top: 50%;
          left: 0;
          right: 0;
          height: 1px;
          background: var(--line-strong);
        }
        .terms-page .tc-corner::after {
          content: '';
          position: absolute;
          left: 50%;
          top: 0;
          bottom: 0;
          width: 1px;
          background: var(--line-strong);
        }
        .terms-page .tc-corner--tl { top: 20px; left: 20px; }
        .terms-page .tc-corner--tr { top: 20px; right: 20px; }
        .terms-page .tc-corner--bl { bottom: 20px; left: 20px; }
        .terms-page .tc-corner--br { bottom: 20px; right: 20px; }

        .terms-page .tc-logo-wrap {
          position: relative;
          z-index: 2;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0.9rem 1.4rem;
          background: rgba(94, 200, 232, 0.05);
          border: 1px solid var(--line-strong);
          border-radius: 8px;
          margin-bottom: 1.75rem;
        }
        .terms-page .tc-logo-wrap img {
          height: 40px;
          width: auto;
        }

        .terms-page .tc-eyebrow {
          position: relative;
          z-index: 2;
          display: inline-block;
          font-size: 0.72rem;
          font-weight: 600;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          color: var(--cyan);
          margin: 0 0 0.6rem;
        }

        .terms-page .tc-hero h1 {
          position: relative;
          z-index: 2;
          font-size: clamp(2.2rem, 5vw, 3.1rem);
          font-weight: 800;
          letter-spacing: -0.01em;
          color: #fff;
          text-shadow: 0 2px 30px rgba(94, 200, 232, 0.25);
          margin: 0 0 0.9rem;
        }

        .terms-page .tc-hero-sub {
          position: relative;
          z-index: 2;
          max-width: 520px;
          margin: 0 auto 1.5rem;
          color: rgba(220, 238, 246, 0.75);
          font-size: 1rem;
          font-weight: 400;
        }

        .terms-page .tc-updated-pill {
          position: relative;
          z-index: 2;
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          background: rgba(94, 200, 232, 0.08);
          border: 1px solid var(--line-strong);
          color: var(--cyan);
          font-size: 0.82rem;
          font-weight: 500;
          padding: 0.4rem 0.9rem;
          border-radius: 999px;
        }

        .terms-page .tc-body {
          max-width: 1120px;
          margin: -3rem auto 0;
          padding: 0 1.5rem 5rem;
          position: relative;
          z-index: 3;
        }

        .terms-page .tc-lead {
          background: #fff;
          border: 1px solid var(--border);
          border-radius: 20px;
          box-shadow: 0 20px 45px -25px rgba(76, 29, 149, 0.35);
          padding: 2.25rem 2.5rem;
          margin-bottom: 2.5rem;
        }
        .terms-page .tc-lead p {
          margin: 0 0 0.9rem;
          color: var(--text-muted);
          font-size: 0.98rem;
        }
        .terms-page .tc-lead p:last-child {
          margin-bottom: 0;
        }
        .terms-page .tc-placeholder {
          font-style: italic;
          color: var(--text-muted);
        }

        .terms-page .tc-layout {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2rem;
          min-width: 0;
        }
        @media (min-width: 1024px) {
          .terms-page .tc-layout {
            grid-template-columns: 250px 1fr;
            align-items: start;
          }
        }

        .terms-page .tc-index {
          background: var(--panel);
          border: 1px solid var(--border);
          border-radius: 18px;
          padding: 1.25rem;
          min-width: 0;
        }
        @media (min-width: 1024px) {
          .terms-page .tc-index {
            position: sticky;
            top: 1.5rem;
            max-height: calc(100vh - 3rem);
            overflow-y: auto;
          }
        }

        .terms-page .tc-index-label {
          display: block;
          font-size: 0.68rem;
          font-weight: 700;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: var(--text-muted);
          margin: 0 0 0.85rem;
          padding: 0 0.4rem;
        }

        .terms-page .tc-index ul {
          list-style: none;
          margin: 0;
          padding: 0;
          display: flex;
          flex-direction: column;
          gap: 0.15rem;
        }
        @media (max-width: 1023px) {
          .terms-page .tc-index ul {
            flex-direction: row;
            overflow-x: auto;
            gap: 0.5rem;
            padding-bottom: 0.25rem;
          }
          .terms-page .tc-index a {
            white-space: nowrap;
          }
        }

        .terms-page .tc-index a {
          display: flex;
          align-items: center;
          gap: 0.55rem;
          font-size: 0.82rem;
          font-weight: 500;
          color: var(--text-muted);
          text-decoration: none;
          padding: 0.5rem 0.6rem;
          border-radius: 10px;
        }
        .terms-page .tc-index a:hover {
          background: #fff;
          color: var(--cyan-deep);
        }
        .terms-page .tc-index a.is-active {
          background: #fff;
          color: var(--cyan-deep);
          font-weight: 600;
          box-shadow: 0 4px 12px -6px rgba(14, 116, 144, 0.5);
        }
        .terms-page .tc-index a span {
          font-size: 0.68rem;
          font-weight: 700;
          color: var(--violet);
        }

        .terms-page .tc-sections {
          display: flex;
          flex-direction: column;
          gap: 1.35rem;
          min-width: 0;
        }

        .terms-page .tc-section {
          background: #fff;
          border: 1px solid var(--border);
          border-radius: 18px;
          padding: 1.85rem 2rem;
          scroll-margin-top: 1.5rem;
        }

        .terms-page .tc-section-head {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1.1rem;
        }
        .terms-page .tc-section-number {
          flex-shrink: 0;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          border-radius: 12px;
          background: linear-gradient(135deg, var(--violet), var(--cyan-deep));
          color: #fff;
          font-weight: 700;
          font-size: 0.95rem;
        }
        .terms-page .tc-section-title {
          font-size: 1.15rem;
          font-weight: 700;
          color: var(--ink);
          margin: 0;
        }

        .terms-page .tc-subhead {
          font-size: 0.92rem;
          font-weight: 600;
          color: var(--cyan-deep);
          margin: 1.1rem 0 0.5rem;
        }

        .terms-page .tc-text {
          margin: 0 0 0.75rem;
          color: var(--text);
          font-size: 0.96rem;
        }
        .terms-page .tc-text:last-child {
          margin-bottom: 0;
        }

        .terms-page .tc-list {
          margin: 0 0 0.75rem;
          padding: 0;
          list-style: none;
          display: flex;
          flex-direction: column;
          gap: 0.45rem;
        }
        .terms-page .tc-list:last-child {
          margin-bottom: 0;
        }
        .terms-page .tc-list li {
          position: relative;
          padding-left: 1.15rem;
          font-size: 0.94rem;
          color: var(--text);
        }
        .terms-page .tc-list li::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0.55em;
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: var(--violet);
        }

        .terms-page .tc-warn {
          display: flex;
          align-items: flex-start;
          gap: 0.7rem;
          background: var(--warn-bg);
          border: 1px solid var(--warn-border);
          border-left: 3px solid var(--warn);
          border-radius: 10px;
          padding: 0.9rem 1.1rem;
          margin: 0.25rem 0 0.75rem;
        }
        .terms-page .tc-warn:last-child {
          margin-bottom: 0;
        }
        .terms-page .tc-warn-badge {
          flex-shrink: 0;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: var(--warn);
          color: #fff;
          font-size: 0.75rem;
          font-weight: 700;
          margin-top: 0.1rem;
        }
        .terms-page .tc-warn p {
          margin: 0;
          color: #7c2d12;
          font-weight: 600;
          font-size: 0.92rem;
        }

        .terms-page .tc-contact {
          background: var(--panel);
          border: 1px solid var(--border);
          border-radius: 14px;
          padding: 1.1rem 1.3rem;
          margin: 0.5rem 0 1rem;
          display: flex;
          flex-direction: column;
          gap: 0.55rem;
        }
        .terms-page .tc-contact-title {
          font-weight: 700;
          color: var(--ink);
          font-size: 0.95rem;
        }
        .terms-page .tc-contact-row {
          display: flex;
          flex-wrap: wrap;
          gap: 0.4rem;
          font-size: 0.9rem;
        }
        .terms-page .tc-contact-label {
          font-weight: 600;
          color: var(--ink);
        }
        .terms-page .tc-contact-value {
          color: var(--text-muted);
          font-style: italic;
        }

        .terms-page .tc-acceptance {
          position: relative;
          overflow: hidden;
          background: var(--ink);
          background-image:
            repeating-linear-gradient(0deg, var(--line) 0 1px, transparent 1px 42px),
            repeating-linear-gradient(90deg, var(--line) 0 1px, transparent 1px 42px);
          border-radius: 20px;
          padding: 2.25rem 2.5rem;
          margin-top: 2rem;
        }
        .terms-page .tc-acceptance-eyebrow {
          position: relative;
          z-index: 2;
          display: block;
          font-size: 0.72rem;
          font-weight: 600;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          color: var(--cyan);
          margin: 0 0 0.7rem;
        }
        .terms-page .tc-acceptance p {
          position: relative;
          z-index: 2;
          color: rgba(220, 238, 246, 0.85);
          font-size: 0.96rem;
          margin: 0 0 1.1rem;
        }
        .terms-page .tc-acceptance ul {
          position: relative;
          z-index: 2;
          list-style: none;
          margin: 0;
          padding: 0;
          display: flex;
          flex-direction: column;
          gap: 0.6rem;
        }
        .terms-page .tc-acceptance li {
          display: flex;
          align-items: flex-start;
          gap: 0.6rem;
          color: #fff;
          font-size: 0.92rem;
          font-weight: 500;
        }
        .terms-page .tc-acceptance li::before {
          content: '✓';
          flex-shrink: 0;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: rgba(94, 200, 232, 0.15);
          border: 1px solid var(--line-strong);
          color: var(--cyan);
          font-size: 0.7rem;
          font-weight: 700;
          margin-top: 0.05rem;
        }

        .terms-page .tc-footer {
          text-align: center;
          margin-top: 3rem;
          padding-top: 2rem;
          border-top: 1px solid var(--border);
          color: var(--text-muted);
          font-size: 0.85rem;
        }

        @media (max-width: 640px) {
          .terms-page .tc-hero {
            padding: 4rem 1.25rem 6rem;
          }
          .terms-page .tc-lead,
          .terms-page .tc-section,
          .terms-page .tc-acceptance {
            padding: 1.5rem 1.4rem;
          }
        }
      `}</style>

      <div className="tc-hero">
        <span className="tc-corner tc-corner--tl" aria-hidden="true" />
        <span className="tc-corner tc-corner--tr" aria-hidden="true" />
        <span className="tc-corner tc-corner--bl" aria-hidden="true" />
        <span className="tc-corner tc-corner--br" aria-hidden="true" />
        <div className="tc-logo-wrap">
          <img loading="lazy" src="/images/logo.png" className="img-fluid" alt="Flarix.ai" />
        </div>
        <h1>Terms &amp; Conditions</h1>
        <p className="tc-hero-sub">
          The agreement between you and Flarix.ai for using our AI floor-planning and design
          services.
        </p>
      </div>

      <div className="tc-body">
        <div className="tc-lead">
          {INTRO_PARAGRAPHS.map((text, i) => (
            <p key={i}>{text}</p>
          ))}
        </div>

        <div className="tc-layout">
          <nav className="tc-index" aria-label="Terms sections">
            <span className="tc-index-label">On this page</span>
            <ul>
              {TERMS_SECTIONS.map((s) => (
                <li key={s.number}>
                  <a
                    href={`#section-${s.number}`}
                    className={activeId === s.number ? 'is-active' : ''}
                  >
                    <span>{s.number}</span>
                    {s.title}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <div className="tc-sections">
            {TERMS_SECTIONS.map((section) => (
              <section
                id={`section-${section.number}`}
                className="tc-section"
                key={section.number}
                data-number={section.number}
                ref={(el) => (sectionRefs.current[section.number] = el)}
              >
                <div className="tc-section-head">
                  <span className="tc-section-number">{section.number}</span>
                  <h2 className="tc-section-title">{section.title}</h2>
                </div>
                {section.blocks.map((block, i) => renderBlock(block, i))}
              </section>
            ))}
          </div>
        </div>

        <div className="tc-acceptance">
          <span className="tc-acceptance-eyebrow">User Acceptance</span>
          <p>{ACCEPTANCE_LEAD}</p>
          <ul>
            {ACCEPTANCE_ITEMS.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </div>

        <div className="tc-footer">© Flarix.ai — All rights reserved.</div>
      </div>
    </div>
  );
}

export default TermsConditions;